import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import { stats, testimonials } from '@/mocks/data';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);
    if (honeypot === '') {
      formData.delete('website_alt');
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9pkefeb4qjhggaepvv0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        if (serverMsg && typeof serverMsg === 'string' && serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <SEO
        title="关于我们 - 91n.com | 品牌介绍与联系方式"
        description="了解91n.com的品牌故事、核心价值观和团队信誉。91n.com致力于为用户提供优质内容和卓越服务，联系我们获取更多信息。"
        keywords="关于91n.com,品牌介绍,联系方式,平台信誉"
        canonicalUrl="https://91n.com/about"
      />

      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://91n.com' },
            { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://91n.com/about' },
          ],
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 91n.com',
          url: 'https://91n.com/about',
          isPartOf: { '@type': 'WebSite', name: '91n.com', url: 'https://91n.com' },
        }}
      />
      <SchemaOrg
        type="FAQPage"
        data={{
          mainEntity: [
            {
              '@type': 'Question',
              name: '91n.com是什么样的平台？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '91n.com是一个综合性在线内容平台，提供科技、生活、教育、财经、健康、旅游、体育等多领域优质内容，致力于为用户创造极致的数字体验。',
              },
            },
            {
              '@type': 'Question',
              name: '91n.com的核心优势是什么？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '我们的核心优势包括安全稳定的技术架构、海量的优质内容资源、便捷流畅的用户体验，以及专属的会员权益体系。',
              },
            },
          ],
        }}
      />

      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100/60 border-b border-background-200/50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mt-4">
              关于 <strong className="text-primary-500">91n.com</strong>
            </h1>
          </div>
        </div>

        <section className="py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 items-start">
              <div className="flex-1">
                <h2 className="text-xl font-bold font-heading text-foreground-900 mb-4">品牌故事</h2>
                <p className="text-foreground-700 leading-relaxed text-sm mb-4">
                  91n.com 诞生于数字时代的浪潮之中，我们深知用户对于高质量内容和便捷服务的渴望。
                  从创立之初，我们就坚持以用户为中心，不断打磨产品体验，汇聚各领域的优质资源，
                  致力于打造一个值得信赖的综合性在线平台。
                </p>
                <p className="text-foreground-700 leading-relaxed text-sm mb-4">
                  经过多年的深耕与发展，91n.com 已经积累了超过500万的忠实用户，
                  覆盖科技数码、生活娱乐、教育学习、财经商业、健康养生、旅游出行、体育运动等七大核心板块，
                  为用户提供丰富多样的内容和便捷高效的服务。
                </p>
                <p className="text-foreground-700 leading-relaxed text-sm">
                  未来，我们将继续秉承"用户至上、内容为王"的理念，持续创新，
                  不断提升平台的服务质量和用户体验，与广大用户共同成长。
                </p>
              </div>
              <div className="lg:w-[380px] flex-shrink-0">
                <div className="rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20minimalist%20office%20interior%20with%20warm%20lighting%20clean%20wooden%20desk%20and%20elegant%20decor%20professional%20creative%20workspace%20atmosphere%20soft%20amber%20tones&width=600&height=400&seq=about-brand&orientation=landscape"
                    alt="91n.com 品牌形象"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-background-100/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-xl font-bold font-heading text-foreground-900 mb-2">核心价值观</h2>
              <p className="text-foreground-600 text-sm">指引我们前行的信念</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: 'ri-shield-star-line', title: '诚信为本', desc: '以诚信为基石，用心服务每一位用户，建立长期信任关系。' },
                { icon: 'ri-lightbulb-flash-line', title: '创新驱动', desc: '持续探索新技术、新模式，为用户创造更多价值。' },
                { icon: 'ri-team-line', title: '合作共赢', desc: '与合作伙伴携手共进，构建健康的内容生态系统。' },
                { icon: 'ri-heart-pulse-line', title: '用户至上', desc: '始终将用户体验放在首位，倾听反馈，持续优化。' },
              ].map((item, idx) => (
                <div key={idx} className="text-center p-6 rounded-lg bg-background-50 border border-background-200/50">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-4">
                    <i className={`${item.icon} text-xl`} />
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-xl font-bold font-heading text-foreground-900 mb-2">平台数据</h2>
              <p className="text-foreground-600 text-sm">数字见证我们的成长</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, idx) => (
                <div key={idx} className="text-center p-6 rounded-lg bg-background-100/70 border border-background-200/50">
                  <div className="text-2xl md:text-3xl font-bold font-heading text-primary-500 mb-1">{stat.value}</div>
                  <div className="text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 bg-background-100/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-xl font-bold font-heading text-foreground-900 mb-2">用户评价</h2>
              <p className="text-foreground-600 text-sm">来自真实用户的反馈</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((item, idx) => (
                <div key={idx} className="p-6 rounded-lg bg-background-50 border border-background-200/50">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                      <img src={item.avatar} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{item.name}</div>
                      <div className="text-xs text-foreground-500">{item.role}</div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">{item.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 bg-background-50">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="text-xl font-bold font-heading text-foreground-900 mb-2">联系我们</h2>
              <p className="text-foreground-600 text-sm">有任何问题或建议？我们期待您的来信</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              {[
                { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@91n.com' },
                { icon: 'ri-phone-line', label: '客服电话', value: '400-888-9191' },
                { icon: 'ri-map-pin-line', label: '公司地址', value: '中国·北京' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-4 rounded-lg bg-background-100/70 border border-background-200/50">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className={`${item.icon} text-lg`} />
                  </div>
                  <div>
                    <div className="text-xs text-foreground-500">{item.label}</div>
                    <div className="text-sm font-medium text-foreground-900">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="space-y-4"
            >
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-field"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-secondary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-secondary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入主题"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-secondary-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                />
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              {formStatus === 'success' && (
                <div className="p-3 rounded-md bg-accent-50 text-accent-700 text-sm border border-accent-200">
                  <i className="ri-checkbox-circle-line mr-1.5" />
                  感谢您的留言！我们将在24小时内回复您。
                </div>
              )}
              {formStatus === 'error' && (
                <div className="p-3 rounded-md bg-secondary-50 text-secondary-700 text-sm border border-secondary-200">
                  <i className="ri-error-warning-line mr-1.5" />
                  {formError}
                </div>
              )}

              <button
                type="submit"
                className="w-full py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                提交留言
              </button>
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}